import React, { useCallback, useMemo, useState } from "react";
import { cloneDeep, set, get, last } from "lodash";
import useTranslations from "../../hooks/useTranslations";
import {
  AnalyticsIcon,
  BriefcaseIcon,
  CollectionIcon,
  MenuIcon as TextLongIcon,
  SelectorMultipleIcon,
  SelectorSingleIcon,
  StarIcon,
  TextShortIcon,
} from "../icons";
import { Button, H3, Modal, ModalActions, ModalBody } from "../wombat-ui";
import { JobSectionModal } from "./JobSectionModal";
import { TemplateQuestionFormInput } from "./TemplateQuestionForm";
import { TemplateSectionFormInput } from "./TemplateSectionForm";
import useViewer from "../../hooks/useViewer";

type TemplateFormElementModalProps = {
  insideSection: boolean;
  open: boolean;
  onClose: () => void;
  getValues: any;
  setValue: any;
  fields: any;
  append?: any;
  insert?: any;
  reset?: any;
  sectionIndex?: number;
  handleFocus: (sectionIdx: number, question: number) => void;
  name: string;
  insertIndex?: number;
};

const getDefaultSectionAttribute = ({
  index = 0,
  type,
}): Partial<TemplateSectionFormInput> => ({
  order: index,
  title: "",
  description: "",
  type,
});

export const getDefaultQuestionAttribute = ({
  index = 0,
  type,
  options,
  referentIds,
}): Partial<TemplateQuestionFormInput> => ({
  order: index,
  label: "",
  options,
  reportTypes: ["FirstReport", "IntermediateReport", "FinalReport"],
  referentIds,
  type,
});

export const TemplateFormElementModal: React.FC<TemplateFormElementModalProps> = ({
  insideSection,
  open,
  onClose,
  getValues,
  setValue,
  fields,
  sectionIndex,
  handleFocus,
  reset,
  name,
  insertIndex = -1,
}) => {
  const t = useTranslations();
  const [jobSectionModalOpen, setJobSectionModalOpen] = useState(false);
  const {
    company: { questionOptionsConfigurations },
    locale,
  } = useViewer();

  const questionOptionsConfiguration = questionOptionsConfigurations.find(
    (a) => a.locale === locale
  );

  const sectionTypes = useMemo(
    () => [
      {
        label: t("templates.sectionType.simple.label"),
        description: t("templates.sectionType.simple.description"),
        value: "TemplateSectionSimple",
      },
      {
        label: t("templates.sectionType.job.label"),
        description: t("templates.sectionType.job.description"),
        value: "TemplateSectionJob",
      },
    ],
    []
  );

  const questionTypes = useMemo(
    () => [
      {
        label: t("templates.answerType.short.label"),
        description: t("templates.answerType.short.description"),
        value: "TemplateQuestionTextShort",
        options: null,
      },
      {
        label: t("templates.answerType.paragraph.label"),
        description: t("templates.answerType.paragraph.description"),
        value: "TemplateQuestionTextLong",
        options: null,
      },
      {
        label: t("templates.answerType.selectorSingle.label"),
        description: t("templates.answerType.selectorSingle.description"),
        value: "TemplateQuestionSelectorSingle",
        options: [],
      },
      {
        label: t("templates.answerType.selectorMultiple.label"),
        description: t("templates.answerType.selectorMultiple.description"),
        value: "TemplateQuestionSelectorMultiple",
        options: [],
      },
      {
        label: t("templates.answerType.evaluation.label"),
        description: t("templates.answerType.evaluation.description"),
        value: "TemplateQuestionEvaluation",
        options: questionOptionsConfiguration.evaluationQuestionOptionsConfiguration.map(
          (e) => ({
            value: e.value,
            label: e.label,
          })
        ),
      },
      {
        label: t("templates.answerType.skillEvaluation.label"),
        description: t("templates.answerType.skillEvaluation.description"),
        value: "TemplateQuestionSkill",
        subjectId: "",
        subjectType: "",
        options: questionOptionsConfiguration.skillQuestionOptionsConfiguration.map(
          (e) => ({
            value: e.value,
            label: e.label,
          })
        ),
      },
    ],
    []
  );

  const getLatestQuestion = useCallback(
    (startAt: number, searchWithin: Record<string, any>) => {
      let lastQuestion;
      let previousSectionIndex = startAt - 1;

      // Guard to avoid unecessary lookup
      if (previousSectionIndex < 0) return [];
      // Loop on every sections trying to find one that does have a question
      while (lastQuestion === undefined && previousSectionIndex >= 0) {
        lastQuestion = last(
          get(
            searchWithin,
            `templateAttributes.templateSectionsAttributes[${previousSectionIndex}]`
          )?.templateQuestionsAttributes
        );

        previousSectionIndex -= 1;
      }

      return lastQuestion;
    },
    []
  );

  const addQuestionOutsideSection = useCallback(
    (value: string, options: any) => {
      // clone it to make mutating the value easier
      const values = cloneDeep(getValues());

      const templateSectionsAttributesName = `${name}.templateSectionsAttributes`;
      const templateSectionsAttributes =
        get(values, templateSectionsAttributesName) || [];

      const getPreviousQuestionReferents = () => {
        let referentIds;
        // The question needs to be appended to the form
        if (insertIndex === -1)
          referentIds =
            getLatestQuestion(templateSectionsAttributes?.length, values)
              ?.referentIds || [];
        // The question is inserted
        // Pick the referents of the last question of the section that was at that position
        else
          referentIds =
            getLatestQuestion(insertIndex, values)?.referentIds || [];

        return referentIds;
      };

      const previousQuestionReferents = getPreviousQuestionReferents();

      templateSectionsAttributes
        .splice(
          insertIndex === -1 ? templateSectionsAttributes.length : insertIndex,
          0,
          {
            ...getDefaultSectionAttribute({
              type: "TemplateSectionBlank",
              index: 0,
            }),
            templateQuestionsAttributes: [
              getDefaultQuestionAttribute({
                type: value,
                index: 0,
                options,
                referentIds: previousQuestionReferents,
              }),
            ],
          }
        )
        .map((i, index) => ({ ...i, index }));

      set(values, templateSectionsAttributesName, templateSectionsAttributes);
      reset(values);

      handleFocus(insertIndex === -1 ? fields.length : insertIndex, 0);
    },
    [
      getValues,
      handleFocus,
      fields.length,
      reset,
      name,
      insertIndex,
    ]
  );

  const addQuestion = useCallback(
    (value: string, options: any) => {
      const values = cloneDeep(getValues());

      const templateQuestionsAttributesName = `${name}.templateQuestionsAttributes`;
      const templateQuestionsAttributes =
        get(values, templateQuestionsAttributesName) || [];

      const getPreviousQuestionReferents = () => {
        let referentIds = [];

        // The question needs to be appended to the section
        if (insertIndex === -1) {
          // The section is empty, search the latest question in the whole form
          if (templateQuestionsAttributes.length === 0)
            referentIds =
              getLatestQuestion(sectionIndex, values)?.referentIds || [];
          else
            referentIds = last(templateQuestionsAttributes)?.referentIds || [];
        }
        // The question is inserted
        // Pick the referents of the question that was at that position
        else
          referentIds =
            templateQuestionsAttributes[insertIndex].referentIds || [];

        return referentIds;
      };

      const previousQuestionReferents = getPreviousQuestionReferents();

      templateQuestionsAttributes.splice(
        insertIndex === -1 ? templateQuestionsAttributes.length : insertIndex,
        0,
        getDefaultQuestionAttribute({
          type: value,
          index: insertIndex === -1 ? fields.length : insertIndex,
          options,
          referentIds: previousQuestionReferents,
        })
      );

      set(values, templateQuestionsAttributesName, templateQuestionsAttributes);
      reset(values);

      handleFocus(
        sectionIndex,
        insertIndex === -1 ? fields.length : insertIndex
      );
    },
    [
      getValues,
      handleFocus,
      sectionIndex,
      fields.length,
      reset,
      name,
      insertIndex,
    ]
  );

  const addSection = useCallback(
    (value: string) => {
      switch (value) {
        case "TemplateSectionSimple": {
          const values = cloneDeep(getValues());
          const templateSectionsAttributesName = `${name}.templateSectionsAttributes`;
          const templateSectionsAttributes =
            get(values, templateSectionsAttributesName) || [];
          templateSectionsAttributes
            .splice(
              insertIndex === -1
                ? templateSectionsAttributes.length
                : insertIndex,
              0,
              {
                ...getDefaultSectionAttribute({
                  type: "TemplateSectionSimple",
                  index: insertIndex === -1 ? fields.length : insertIndex,
                }),
              }
            )
            .map((i, index) => ({ ...i, index }));

          set(
            values,
            templateSectionsAttributesName,
            templateSectionsAttributes
          );
          reset(values);

          onClose();
          return;
        }
        case "TemplateSectionJob":
          setJobSectionModalOpen(true);
          return;
        default:
          return null;
      }
    },
    [sectionTypes, fields.length, getValues, setValue, insertIndex]
  );

  const questionIcon = useCallback((type: string) => {
    const className = "w-5 h-5 text-primary-500";
    switch (type) {
      case "TemplateQuestionTextShort":
        return <TextShortIcon iconClassName={className} />;
      case "TemplateQuestionTextLong":
        return <TextLongIcon iconClassName={className} />;
      case "TemplateQuestionSelectorSingle":
        return <SelectorSingleIcon iconClassName={className} />;
      case "TemplateQuestionSelectorMultiple":
        return <SelectorMultipleIcon iconClassName={className} />;
      case "TemplateQuestionSkill":
        return <AnalyticsIcon iconClassName={className} />;
      case "TemplateQuestionEvaluation":
        return <StarIcon iconClassName={className} />;
      default:
        return null;
    }
  }, []);

  const sectionIcon = useCallback((type: string) => {
    const className = "w-5 h-5 text-primary-500";
    switch (type) {
      case "TemplateSectionSimple":
        return <CollectionIcon iconClassName={className} />;
      case "TemplateSectionJob":
        return <BriefcaseIcon iconClassName={className} />;
      default:
        return null;
    }
  }, []);

  return (
    <>
      <Modal isOpen={open} aria-label="Template form element creation modal">
        <ModalBody>
          <div className="pt-3">
            <H3>
              {insideSection
                ? t("templates.createSectionQuestion")
                : t("templates.createQuestion")}
            </H3>
            {questionTypes.map(({ label, description, value, options }) => (
              <div
                className="flex items-center px-6 py-4 my-2 space-x-6 border rounded hover:bg-primary-100"
                key={value}
                id={`questionButton${value}`}
                onClick={() => {
                  insideSection
                    ? addQuestion(value, options)
                    : addQuestionOutsideSection(value, options);
                  onClose();
                }}
                role="button"
              >
                <span className="flex items-center justify-center col-span-1">
                  {questionIcon(value)}
                </span>
                <div>
                  <span className="block text-sm font-medium">{label}</span>
                  <span>{description}</span>
                </div>
              </div>
            ))}
            {!insideSection && (
              <>
                <H3>{t("templates.createSection")}</H3>
                {sectionTypes.map(({ label, description, value }) => (
                  <div
                    className="flex items-center px-6 py-4 my-2 space-x-6 border rounded hover:bg-primary-100"
                    key={value}
                    id={`sectionButton${value}`}
                    onClick={() => {
                      addSection(value);
                    }}
                    role="button"
                  >
                    <span className="flex items-center justify-center col-span-1">
                      {sectionIcon(value)}
                    </span>
                    <div>
                      <span className="block text-sm font-medium">{label}</span>
                      <span>{description}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </ModalBody>
        <ModalActions>
          <Button
            variant="subtle"
            onClick={onClose}
            id={`addElementModalCancel`}
          >
            {t("forms.actions.cancel")}
          </Button>
        </ModalActions>
      </Modal>
      <JobSectionModal
        open={jobSectionModalOpen}
        onClose={() => {
          setJobSectionModalOpen(false);
          onClose();
        }}
        getValues={getValues}
        setValue={setValue}
        fields={fields}
        insertIndex={insertIndex}
        reset={reset}
        name={name}
      />
    </>
  );
};
